import { useRef, useEffect, useState, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { Play, Pause, Volume2, VolumeX, RotateCcw, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/lib/utils'
import { API_BASE_URL } from '@/lib/constants'

interface AudioWavePlayerProps {
  src: string
  className?: string
}

export function AudioWavePlayer({ src, className }: AudioWavePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const mountedRef = useRef(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.8)
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [playbackRate, setPlaybackRate] = useState(1)

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    // Track mount state to prevent state updates after unmount
    mountedRef.current = true

    if (!containerRef.current || !src) return

    setIsLoading(true)
    setError(null)

    // Small delay to handle React StrictMode double-mount
    // This ensures previous instance is fully cleaned up before creating new one
    const initTimeout = setTimeout(() => {
      if (!mountedRef.current || !containerRef.current) return

      // Clean up any existing instance first
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy()
        } catch {
          // Ignore cleanup errors
        }
        wavesurferRef.current = null
      }

      const wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: '#94a3b8',
        progressColor: '#3b82f6',
        cursorColor: '#1d4ed8',
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        height: 60,
        normalize: true,
        backend: 'WebAudio',
      })

      wavesurferRef.current = wavesurfer

      wavesurfer.on('ready', () => {
        if (!mountedRef.current) return
        setIsLoading(false)
        setDuration(wavesurfer.getDuration())
        wavesurfer.setVolume(volume)
      })

      wavesurfer.on('audioprocess', () => {
        if (!mountedRef.current) return
        setCurrentTime(wavesurfer.getCurrentTime())
      })

      wavesurfer.on('play', () => mountedRef.current && setIsPlaying(true))
      wavesurfer.on('pause', () => mountedRef.current && setIsPlaying(false))
      wavesurfer.on('finish', () => mountedRef.current && setIsPlaying(false))

      wavesurfer.on('error', (err: Error | string) => {
        // Ignore errors if component unmounted (abort errors from cleanup)
        if (!mountedRef.current) return

        console.error('WaveSurfer error:', err)
        setIsLoading(false)
        // Provide more descriptive error message
        const errorMessage = typeof err === 'string' ? err : err?.message || 'Unknown error'

        // Ignore abort errors - these happen during normal cleanup in StrictMode
        if (errorMessage.includes('abort') || errorMessage.includes('signal')) {
          return
        }

        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('network')) {
          setError('Failed to load audio - network error')
        } else if (errorMessage.includes('decode') || errorMessage.includes('format')) {
          setError('Failed to decode audio - unsupported format')
        } else {
          setError(`Failed to load audio: ${errorMessage}`)
        }
      })

      // Use audio proxy to avoid CORS/authentication issues with external audio URLs (like Bland.ai)
      const proxyUrl = `${API_BASE_URL}/api/dashboard/audio-proxy?url=${encodeURIComponent(src)}`
      wavesurfer.load(proxyUrl)
    }, 50) // Small delay to allow StrictMode cleanup

    return () => {
      mountedRef.current = false
      clearTimeout(initTimeout)
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy()
        } catch {
          // Ignore cleanup errors
        }
        wavesurferRef.current = null
      }
    }
  }, [src])

  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(isMuted ? 0 : volume)
    }
  }, [volume, isMuted])

  const togglePlay = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause()
    }
  }, [])

  const restart = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.seekTo(0)
      wavesurferRef.current.play()
    }
  }, [])

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev)
  }, [])

  const cyclePlaybackRate = useCallback(() => {
    const rates = [1, 1.5, 2]
    const currentIndex = rates.indexOf(playbackRate)
    const nextRate = rates[(currentIndex + 1) % rates.length]
    setPlaybackRate(nextRate)
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(nextRate)
    }
  }, [playbackRate])

  const handleVolumeChange = useCallback((values: number[]) => {
    const newVolume = values[0]
    setVolume(newVolume)
    if (newVolume > 0 && isMuted) {
      setIsMuted(false)
    }
  }, [isMuted])

  if (error) {
    return (
      <div className={cn('p-4 bg-muted rounded-lg', className)}>
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" asChild>
            <a href={src} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </a>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('p-4 bg-muted rounded-lg space-y-3', className)}>
      {/* Waveform container */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted rounded">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <div ref={containerRef} className={cn(isLoading && 'invisible')} />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        {/* Play/Pause button */}
        <Button
          variant="outline"
          size="icon"
          onClick={togglePlay}
          disabled={isLoading}
          className="h-10 w-10 rounded-full"
        >
          {isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" />
          )}
        </Button>

        {/* Restart button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={restart}
          disabled={isLoading}
          className="h-8 w-8"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>

        {/* Playback speed */}
        <Button
          variant="ghost"
          size="sm"
          onClick={cyclePlaybackRate}
          disabled={isLoading}
          className="h-7 px-2 text-xs font-mono font-semibold min-w-[36px]"
          title="Playback speed"
        >
          {playbackRate}x
        </Button>

        {/* Time display */}
        <div className="flex-1 flex items-center gap-2 text-sm text-muted-foreground font-mono">
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Volume controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMute}
            className="h-8 w-8"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          <Slider
            value={[isMuted ? 0 : volume]}
            max={1}
            step={0.1}
            onValueChange={handleVolumeChange}
            className="w-20"
          />
        </div>

        {/* External link */}
        <Button variant="ghost" size="icon" asChild className="h-8 w-8">
          <a href={src} target="_blank" rel="noopener noreferrer" title="Open in new tab">
            <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  )
}

export default AudioWavePlayer
